import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { Court } from "../../api/types";
import useShowToast from "../../hooks/useShowToast";
import ConfirmModal from "../ui/confirm-modal";

type CourtFormData = {
  name: string;
  is_indoor: boolean;
};

type SettingsCourtsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  court?: Court;
};

const SettingsCourtsModal = ({ isOpen, onClose, court }: SettingsCourtsModalProps) => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useShowToast();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CourtFormData>();

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: OnConfirmModalClose,
  } = useDisclosure();

  const onSubmit = async (data: CourtFormData) => {
    setIsLoading(true);
    if (court?.id) {
      await api.updateCourt(court.id, { ...data });
    } else {
      await api.createCourt({ ...data });
    }

    if (api.error) {
      console.log("Error updating court", api.error);
      setIsLoading(false);
      errorToast({
        title: t("settings.courts.error_title"),
        description: t("settings.courts.error_message.default"),
      });
      return;
    }
    setIsLoading(false);
    onClose();

    successToast({
      title: t("settings.courts.success_title"),
      description: t("settings.courts.success_message"),
    });
  };

  const deleteCourt = async () => {
    if (!court?.id) {
      return;
    }

    setIsLoading(true);
    await api.deleteCourt(court.id);

    if (api.error) {
      console.log("Error deleting court", api.error);
      setIsLoading(false);
      errorToast({
        title: t("settings.courts.error_title"),
        description: t("settings.courts.error_message.default"),
      });
      return;
    }
    setIsLoading(false);
    onClose();

    successToast({
      title: t("settings.courts.modal.delete_success_title"),
      description: t("settings.courts.modal.delete_success_message"),
    });
  };

  useEffect(() => {
    reset({
      name: court?.name || "",
      is_indoor: court?.is_indoor || false,
    });
  }, [court, reset]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent maxW="500px">
          {/* Modal title */}
          <ModalHeader>
            {court ? t("settings.courts.modal.edit_title") : t("settings.courts.modal.add_title")}
          </ModalHeader>

          <Divider />

          {/* Modal Body */}
          <ModalBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>{t("settings.courts.modal.name")}</FormLabel>
                  <Input
                    {...register("name")}
                    placeholder={t("settings.courts.modal.name_placeholder")}
                    required
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">{t("settings.courts.modal.is_indoor")}</FormLabel>
                  <Switch {...register("is_indoor")} required />
                </FormControl>
              </VStack>
            </form>
          </ModalBody>

          <Divider />

          {/* Modal Footer with buttons */}
          <ModalFooter>
            {court && (
              <Box mr="auto">
                <Button colorScheme="red" onClick={onConfirmModalOpen}>
                  {t("settings.courts.modal.delete")}
                </Button>
              </Box>
            )}

            <Button variant="ghost" onClick={onClose}>
              {t("settings.courts.modal.cancel")}
            </Button>

            <Button
              colorScheme="blue"
              ml={3}
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading}
            >
              {court ? t("settings.courts.modal.edit") : t("settings.courts.modal.add")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={OnConfirmModalClose}
        onConfirm={() => deleteCourt()}
        colorScheme="red"
        message={t("settings.courts.modal.delete_message", { courtName: court?.name })}
      />
    </>
  );
};

export default SettingsCourtsModal;
